import React from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { BoxIcon, CircleCheckIcon, CircleSlashIcon, TagIcon } from 'lucide-react'

/**
 * Shared tag row preview: glyph + a NAME on the primary line, the chip UID beneath it.
 *
 * WHY IT EXISTS. Every list that showed a tag led with the raw UID — `041F67A2001990` as the
 * primary and only text — while every other list in the platform leads with a name and carries
 * the identifier as secondary detail (`PaymentMethodPreview`, `AddressPreview`, `CompanyCell`,
 * `UserBlock`). A hex string as a headline is the least recognisable thing the row could open
 * with: it is 14 characters that differ from their neighbours in the middle, so a list of them
 * reads as one texture and the operator has to compare digit by digit.
 *
 * THE UID IS KEPT, DELIBERATELY. In a Create-Certificates session the tags are blank — no
 * certificate, no owner, and every row is the same product — so the UID is the ONLY thing that
 * tells two rows apart, and it is what an operator matches against the physical tag in their
 * hand when a row says the tag is already assigned and they have to find and free that one. It
 * moves to the subline; it does not go away. (It is also not a secret: any NFC reader can read
 * a UID off any chip. The secret is the CMAC key, which never leaves the server.)
 *
 * ⚠️ CAPABILITY, NEVER THE CHIP FAMILY. `capability` is "Verify" / "Connect" / "Verify + Tamper"
 * — what the tag DOES. The family (NTAG 424 DNA, NTAG 213, EM4425) is internal fulfilment
 * detail and there is a standing rule against putting it on any operator- or customer-facing
 * surface: brands are customers, not staff. The API derives capability from the tag-shop SKU
 * server-side (`parseTagSku`) for exactly this reason — a client cannot leak a value it was
 * never given — so this component takes the derived string and must never be extended to take
 * a family.
 *
 * THE 40px CIRCULAR AVATAR OF `PaymentMethodPreview` / `AddressPreview` IS NOT USED HERE, on
 * purpose. Those render in table cells and pickers with room to spare; the first caller is a
 * scrolling review list capped at 208px, where a 40px avatar plus two text lines would show
 * roughly three rows at a time. The structure that matters — one glyph, a name, an identifier
 * beneath — is the same. If a spacious caller ever appears, add the variant then rather than
 * guessing at one now.
 */
export interface TagPreviewProps {
  /** The chip's own UID, as read off the physical tag. Rendered as the subline, monospace. */
  uid: string
  /**
   * An explicit name for the record this tag resolves to, when one is known — it wins over
   * `capability`, because a specific name beats a class of thing. Absent for a blank tag.
   */
  label?: string | null
  /**
   * What the tag can do — "Verify", "Connect", "Verify + Tamper". Rendered as `<capability> tag`.
   * Capability only; see the class comment. Null/absent falls through to the neutral label.
   */
  capability?: string | null
  /**
   * The package this row was expanded from, shown on the subline beside the UID: both are
   * "which physical thing is this", so they belong on the same line. Name when the server could
   * read one, a shortened id otherwise.
   */
  packageLabel?: string | null
  /** Tint applied to both lines — used by a row whose outcome is a refusal. */
  color?: string
  /**
   * Per-row OUTCOME, drawn as the leading glyph.
   *
   * Omitted (the default) keeps the neutral tag glyph, for lists that are just naming tags.
   * In a SCAN list every row is the result of an action, and the leading position is the one
   * the eye runs down — so a fixed tag icon there spends the most scannable column in the
   * list repeating a fact ("this is a tag") that the row's own label already states, while
   * the thing an operator is actually looking for (did this one take?) is only findable by
   * reading the right-hand side of each row.
   *
   * `ok` and `blocked` are distinguished by SHAPE as well as colour — a check versus a
   * slashed circle — so the outcome survives being read at a glance, on a warehouse screen,
   * or by someone who cannot separate the two hues.
   */
  status?: 'ok' | 'blocked'
}

/**
 * The one place the primary line is decided, so no caller re-invents the ladder.
 *
 * Falls all the way to a neutral noun rather than to the UID: a row that cannot name itself is
 * still better off saying "Vera tag" with its UID beneath than promoting the hex back to the
 * headline, because the two-line shape is what makes the list scannable in the first place.
 */
export const tagPreviewLabel = (params: {
  label?: string | null
  capability?: string | null
}): string => {
  const { label, capability } = params
  if (label && label.trim()) return label.trim()
  if (capability && capability.trim()) return `${capability.trim()} tag`
  return 'Vera tag'
}

export const TagPreview = ({
  uid,
  label,
  capability,
  packageLabel,
  color,
  status,
}: TagPreviewProps) => {
  const theme = useTheme()

  // strokeWidth 1.5 on the outcome glyphs, not the tag glyph's 1: at 16px a check inside a
  // circle loses its tick to hairline rendering, and the two states stop being separable at
  // exactly the size they are read at.
  const LeadingGlyph =
    status === 'ok' ? (
      <CircleCheckIcon
        strokeWidth={1.5}
        size={16}
        color={theme.palette.success.main}
        style={{ flexShrink: 0 }}
      />
    ) : status === 'blocked' ? (
      <CircleSlashIcon
        strokeWidth={1.5}
        size={16}
        color={theme.palette.error.main}
        style={{ flexShrink: 0 }}
      />
    ) : (
      <TagIcon strokeWidth={1} size={16} style={{ flexShrink: 0 }} />
    )

  return (
    <Stack direction="row" spacing={3} alignItems="center" sx={{ minWidth: 0 }}>
      {LeadingGlyph}
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body3" noWrap sx={color ? { color } : undefined}>
          {tagPreviewLabel({ label, capability })}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          {/* The UID does NOT shrink; the package label beside it does. Both being shrinkable
              split the loss between them, so a package-sourced row truncated its own UID
              ("SCANDEMO-…") to make room for context — throwing away the row's identity to
              keep its footnote. */}
          <Typography
            variant="body4"
            noWrap
            sx={{
              fontFamily: 'monospace',
              flexShrink: 0,
              color: color || theme.palette.text.secondary,
            }}
          >
            {uid}
          </Typography>
          {packageLabel && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0, color: color || theme.palette.text.secondary }}
            >
              <BoxIcon strokeWidth={1} size={12} style={{ flexShrink: 0 }} />
              <Typography variant="body4" noWrap sx={{ color: 'inherit' }}>
                {packageLabel}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
